import { useTranslation } from 'react-i18next'
import { CheckBoxOutlineBlank, Delete, DragHandle, ExpandMore } from '@mui/icons-material'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import { Box } from '@mui/system'
import PropTypes from 'prop-types'

import { MiscItemIcon } from '@/common/icons'

const classes = {
    Overlay: {
        borderRadius: 2,
        overflow: 'hidden',
        backgroundColor: 'action.hover',
    },

    OverlayContent: {
        px: 2,
        py: 1.5,
        display: 'flex',
        justifyContent: 'space-between',
        height: 74,
    },

    OverlayContentLeft: {
        gap: 1.5,
    },

    OverlayContentRight: {
        gap: 6,
    },

    OverlayContentColor: {
        color: 'text.secondary',
    },
}

/**
 * Placeholder component displayed during fast scrolling in the MiscellaneousItemList.
 * Used by react-virtuoso's ScrollSeekPlaceholder to show lightweight placeholders
 * instead of full MiscItem components when the user scrolls rapidly.
 *
 * @param {Object} props - Component props
 * @param {number} props.height - The height of the placeholder (provided by Virtuoso)
 * @param {number} props.index - The index of the item in the list (provided by Virtuoso)
 * @returns {React.ReactElement} A lightweight placeholder mimicking a misc item row
 */
const SeekMiscItemOverlay = ({ height, index }) => {
    const { t } = useTranslation()

    return (
        <Box
            id={index}
            index={index}
            sx={classes.Overlay}
        >
            <Box sx={classes.OverlayContent}>
                <Box
                    sx={[
                        {
                            alignItems: 'center',
                            display: 'flex',
                        },
                        classes.OverlayContentLeft,
                    ]}
                >
                    <IconButton
                        size="small"
                        sx={classes.OverlayContentColor}
                    >
                        <DragHandle />
                    </IconButton>

                    <CheckBoxOutlineBlank
                        fontSize="small"
                        sx={classes.OverlayContentColor}
                    />

                    <Box
                        sx={{
                            alignItems: 'center',
                            display: 'flex',
                            gap: 2,
                        }}
                    >
                        <MiscItemIcon sx={classes.OverlayContentColor} />
                        <Typography sx={classes.OverlayContentColor}>{t('loading...')}</Typography>
                    </Box>
                </Box>

                <Box
                    sx={[
                        {
                            alignItems: 'center',
                            display: 'flex',
                        },
                        classes.OverlayContentRight,
                    ]}
                >
                    <Box
                        sx={{
                            alignItems: 'center',
                            display: 'flex',
                        }}
                    >
                        <IconButton sx={classes.OverlayContentColor}>
                            <Delete />
                        </IconButton>

                        <ExpandMore sx={classes.OverlayContentColor} />
                    </Box>
                </Box>
            </Box>
        </Box>
    )
}

SeekMiscItemOverlay.propTypes = {
    height: PropTypes.number.isRequired,
    index: PropTypes.number.isRequired,
}

export default SeekMiscItemOverlay
